// This script is called from the module itself so the path must be here.
import { sPostToEngagementTools } from './shared.min.js';

class ConfusedAISearch {
	constructor(widget) {
		this.widget = widget;

		this.apiUrl = '';
		this.apiSubKey = '';

		this.searchForm = widget.querySelector('.mod-ai-search--form');
		this.submitBtn = widget.querySelector('.mod-ai-search--submit');
		this.input = widget.querySelector('.mod-ai-search--input');
		this.errorContainer = widget.querySelector('.mod-ai-search--error');

		this.timeoutErrorText = document.getElementById('mod-ai-search--timeout-error');
		this.defaultErrorText = document.getElementById('mod-ai-search--default-error');

		this.responseContainer = widget.querySelector('.mod-ai-search--response');
		this.answerContainer = widget.querySelector('.mod-ai-search--answer');
		this.firstLinkContainer = widget.querySelector('.mod-ai-search--first-link');
		this.helpfulLinksContainer = widget.querySelector('.mod-ai-search--helpful-links');
		this.linksContainer = widget.querySelector('.mod-ai-search--links');
		this.loadingTextPlaceholder = widget.querySelector('.mod-ai-search--loading-text');
		this.loadingStates = widget
			.querySelector('#loading-states')
			.textContent.split('|')
			.map(s => s.trim());
		this.resetBtn = widget.querySelector('.mod-ai-search--reset-btn');
		this.noAnswerNode = widget.querySelector('#mod-ai-search--no-answer-template').content;
		this.rewardsBanner = widget.querySelector('.mod-ai-search__rewards-banner');

		// Typing animation state
		this.stateIndex = 0;
		this.charIndex = 0;
		this.typingSpeed = 50; // milliseconds per character
		this.loadingTypingSpeed = 750; // milliseconds per character when loading
		this.typingInterval = null;

		this.valid = true;
		this.loading = false;

		const sessionStorageThreadIdKey = 'confused-ai-search-thread-id';
		this.getThreadId = () => sessionStorage.getItem(sessionStorageThreadIdKey) || null;
		this.setThreadId = id => sessionStorage.setItem(sessionStorageThreadIdKey, id);
		this.pushThreadIdToDataLayer = id => {
			if (window.dataLayer) {
				const dataLayerPropertyName = 'searchThreadId';
				const dataLayerEventName = 'ask_confused_thread_initiated';
				const existing = window.dataLayer.find(entry => entry.hasOwnProperty(dataLayerPropertyName));
				if (!existing || existing[dataLayerPropertyName] !== id)
					window.dataLayer.push({ event: dataLayerEventName, [dataLayerPropertyName]: id });
			}
		};

		this.scrollToTopOfTarget = target => {
			const offset = 110;
			const topPos = target.getBoundingClientRect().top + window.pageYOffset - offset;

			window.scrollTo({
				top: topPos,
				behavior: 'smooth',
			});
		};

		this.revealAnswer = () => {
			this.responseContainer.classList.remove('display-none');
			this.resetBtn.classList.remove('display-none');
			this.scrollToTopOfTarget(this.responseContainer);
		};

		this.getApiDetails();
		this.createObserver();
		this.bindEvents();
	}

	getApiDetails() {
		const endpointSpan = document.getElementById('api-endpoint');
		this.apiUrl = endpointSpan ? endpointSpan.textContent : 'http://localhost:3000/foundry-ai/search-bot';
		const subKeySpan = document.getElementById('sub-key');
		this.apiSubKey = subKeySpan ? subKeySpan.textContent : '';
	}

	animateLoadingText() {
		const loadingEffectDots = '...';
		const loadingStateText = this.loadingStates[this.stateIndex] + loadingEffectDots;

		// Type effect through the current loading state text
		if (this.charIndex < loadingStateText.length) {
			// When we get to the loading effect dots, slow down the typing speed to emulate loading
			if (this.charIndex >= this.loadingStates[this.stateIndex].length) {
				this.resetInterval(this.loadingTypingSpeed);
			}

			// Append the next character and move on
			this.loadingTextPlaceholder.textContent += loadingStateText[this.charIndex];
			this.charIndex++;
		} else {
			// reset to normal typing speed
			this.resetInterval(this.typingSpeed);

			// Move to the next loading state
			this.stateIndex++;
			this.charIndex = 0;

			// If we've cycled through all loading states, stop the animation
			if (this.stateIndex >= this.loadingStates.length) {
				clearInterval(this.typingInterval);
				return;
			}

			// Clear the text for the next state
			this.loadingTextPlaceholder.textContent = '';
		}
	}

	resetInterval(typingSpeed) {
		clearInterval(this.typingInterval);
		this.typingInterval = setInterval(() => this.animateLoadingText(), typingSpeed);
	}

	initLoadingEffect() {
		this.stateIndex = 0;
		this.charIndex = 0;
		this.loadingTextPlaceholder.textContent = '';
		this.resetInterval(this.typingSpeed);
		this.loadingTextPlaceholder.classList.remove('display-none');
	}

	bindEvents() {
		if (!this.searchForm || !this.input || !this.submitBtn) return;

		this.input.addEventListener('input', () => {
			const query = this.input.value.trim();
			this.valid = query.length >= 3;

			if (this.valid) {
				this.submitBtn.removeAttribute('disabled');
			} else {
				this.submitBtn.setAttribute('disabled', 'disabled');
			}

			this.input.style.height = 'auto';
			this.input.style.height = `${this.input.scrollHeight}px`;
			this.clearError();
		});

		this.input.addEventListener('keydown', e => {
			if (e.key === 'Enter' && this.valid) {
				e.preventDefault();
				this.submitBtn.click();
			}
		});

		this.searchForm.addEventListener('submit', async e => {
			e.preventDefault();
			if (this.loading || !this.valid) return;

			this.loading = true;

			this.initLoadingEffect();

			this.input.setAttribute('disabled', 'disabled');
			this.submitBtn.classList.add('loading');

			this.clearError();

			const query = this.input.value.trim();
			if (!query) return;

			this.resetUI();

			try {
				const res = await sPostToEngagementTools(
					{ message: query, threadId: this.getThreadId() },
					this.apiSubKey,
					this.apiUrl
				);

				if (res.status === 404) {
					// Handle 404 specifically - the bot did not find an answer
					const data = await res.json();
					const { threadId } = data;
					if (threadId) {
						this.setThreadId(threadId);
						this.pushThreadIdToDataLayer(threadId);
					}
					if (this.answerContainer) this.answerContainer.appendChild(this.noAnswerNode.cloneNode(true));
					this.revealAnswer();
					return;
				}

				if (!res.ok) {
					const e = new Error(`HTTP ${res.status} ${res.statusText}`);
					e.status = res.status;
					throw e;
				}

				const data = await res.json();
				const { message, links, threadId, categories } = data;

				this.handleCategories(categories);

				if (this.answerContainer) this.answerContainer.textContent = message;

				if (links?.length) {
					this.populateLinks(links);
				}

				this.setThreadId(threadId);
				this.pushThreadIdToDataLayer(threadId);

				this.revealAnswer();
			} catch (err) {
				switch (err.status) {
					case 504:
						this.showError(
							this.timeoutErrorText?.textContent || 'The request timed out. Please try again.'
						);
						break;
					default:
						this.showError(
							this.defaultErrorText?.textContent || 'Something went wrong. Please try again later.'
						);
				}
			} finally {
				this.submitBtn.classList.remove('loading');
				this.input.removeAttribute('disabled');
				this.loading = false;
				this.loadingTextPlaceholder.classList.add('display-none');
				clearInterval(this.typingInterval);
			}
		});

		this.resetBtn.addEventListener('click', () => {
			this.resetUI();
			this.input.value = '';
			this.input.style.height = 'auto';
			this.input.removeAttribute('disabled');
			this.submitBtn.setAttribute('disabled', 'disabled');
			this.valid = true;
			this.input.focus();
			this.scrollToTopOfTarget(this.searchForm);
		});
	}

	handleCategories(categories) {
		if (!categories || !Array.isArray(categories) || categories.length === 0) return;
		if (categories.includes('rewards')) {
			this.rewardsBanner?.classList.remove('display-none');
		}
	}

	resetUI() {
		this.answerContainer.textContent = '';
		this.responseContainer.classList.add('display-none');
		this.firstLinkContainer.innerHTML = '';
		this.firstLinkContainer.classList.add('display-none');
		this.linksContainer.innerHTML = '';
		this.helpfulLinksContainer.classList.add('display-none');
		this.resetBtn.classList.add('display-none');
		this.rewardsBanner?.classList.add('display-none');
	}

	populateLinks(links) {
		const [first, ...rest] = links;

		if (first) {
			const firstLinkElement = document.createElement('a');
			firstLinkElement.href = first.link;
			firstLinkElement.target = '_blank';
			firstLinkElement.rel = 'noopener noreferrer';
			firstLinkElement.classList.add('bold');

			const linkWords = first.linkTitle.trim().split(' ');
			const lastWord = linkWords.pop();
			const firstPart = linkWords.join(' ');

			const textNode = document.createTextNode(`${firstPart} `);
			firstLinkElement.appendChild(textNode);

			const nowrapSpan = document.createElement('span');
			nowrapSpan.style.whiteSpace = 'nowrap';

			const lastWordNode = document.createTextNode(`${lastWord} `);
			const arrowSpan = document.createElement('span');
			arrowSpan.classList.add('link-arrow');

			nowrapSpan.appendChild(lastWordNode);
			nowrapSpan.appendChild(arrowSpan);
			firstLinkElement.appendChild(nowrapSpan);

			const firstLinkLi = document.createElement('li');
			firstLinkLi.appendChild(firstLinkElement);

			this.firstLinkContainer.appendChild(firstLinkLi);
			this.firstLinkContainer.classList.remove('display-none');
		}

		if (rest.length > 0) {
			rest.forEach(({ linkTitle, link }) => {
				const a = document.createElement('a');
				a.href = link;
				a.textContent = linkTitle;
				a.target = '_blank';
				a.rel = 'noopener noreferrer';

				const li = document.createElement('li');
				li.appendChild(a);
				this.linksContainer.appendChild(li);
			});

			this.helpfulLinksContainer.classList.remove('display-none');
		}
	}

	showError(msg) {
		this.errorContainer.textContent = msg;
		this.errorContainer.classList.remove('display-none');
	}

	clearError() {
		this.errorContainer?.classList.add('display-none');
		if (this.errorContainer) this.errorContainer.textContent = '';
	}

	createObserver() {
		const observer = new IntersectionObserver(entries => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					const fromTop = entry.intersectionRect.top;
					const fromBottom = entry.rootBounds.height - entry.intersectionRect.bottom;
					if (fromTop > fromBottom) {
						entry.target.classList.add('nudge--visible');
					}
				} else {
					entry.target.classList.remove('nudge--visible');
				}
			}
		});
		this.widget.querySelectorAll('.nudge').forEach(el => observer.observe(el));
	}
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.mod-ai-search').forEach(widget => {
		new ConfusedAISearch(widget);
	});
});
